import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Edit3 } from "lucide-react";
import {
  useFetchRiskLevelsMutation,
  useUpdateRiskLevelMutation,
} from "../../../../features/criterion/criterionApiSlice";
import { selectUser } from "../../../../features/auth/authSlice";
import { useSelector } from "react-redux";

const ProgramFrequency = ({ isWelcomePage }) => {
  const user = useSelector(selectUser);
  const [fetchRiskLevels, { isLoading: isFetchingRiskLevels }] =
    useFetchRiskLevelsMutation();
  const [updateRiskLevel, { isLoading: isUpdatingRiskLevel }] =
    useUpdateRiskLevelMutation();
  const [riskLevels, setRiskLevels] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [frequencyInput, setFrequencyInput] = useState("");
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");
  const isSaving = isFetchingRiskLevels || isUpdatingRiskLevel;
  const extractRiskLevels = (res) =>
    res?.data ?? res?.data?.data ?? res?.result ?? res ?? [];

  const loadRiskLevels = useCallback(async () => {
    try {
      const res = await fetchRiskLevels().unwrap();
      setRiskLevels(extractRiskLevels(res));
      setPageError("");
    } catch (err) {
      setPageError(err?.data?.message || "Failed to load risk levels.");
      setRiskLevels([]);
    }
  }, [fetchRiskLevels]);

  useEffect(() => {
    loadRiskLevels();
  }, [loadRiskLevels]);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRisk(null);
    setFrequencyInput("");
    setError("");
  };

  const openEditModal = (risk) => {
    setSelectedRisk(risk);
    setFrequencyInput(risk.rotation);
    setModalOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const value = Number(frequencyInput);

    if (Number.isNaN(value) || value <= 0) {
      setError("Please enter a valid number of days greater than 0.");
      return;
    }

    try {
      await updateRiskLevel({ id: selectedRisk.id, rotation: value }).unwrap();

      loadRiskLevels();
    } catch (err) {
      setError(err?.data?.message || "Failed to update risk level.");
    }

    closeModal();
  };

  const filteredRiskLevels = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return riskLevels;
    return riskLevels.filter((risk) => risk.name.toLowerCase().includes(term));
  }, [riskLevels, search]);

  return (
    <div
      className={`relative flex flex-col w-full ${
        isWelcomePage ? "":"overflow-hidden"}`}
    >
      <div className="flex flex-col gap-3 px-4 py-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-3 items-center">
          <p className="m-0 text-2xl font-semibold text-gray-900">
            Risk Level Frequency Configuration
          </p>
          <p className="m-0 text-sm text-gray-500">
            {filteredRiskLevels.length} Risk Levels
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px] sm:max-w-sm">
            <input
              type="text"
              placeholder="Search risk level..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pr-3 pl-3 w-full h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {user?.permissions?.includes('program_frequency.view_program_frequency') && (
        <div className="flex-1 px-4 pt-5 pb-6">
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-600">
              <tr>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Risk Level ID
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Rotation Frequency
                </th>
                {user?.permissions?.includes('program_frequency.edit_program_frequency') && (
                  <th className="px-6 py-3 text-sm font-medium text-right text-white">
                    Edit
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRiskLevels.length === 0 ? (
                <tr>
                  <td
                    colSpan={user?.permissions?.includes('program_frequency.edit_program_frequency') ? 4 : 3}
                    className="px-6 py-8 text-sm text-center text-gray-500"
                  >
                    No risk levels found.
                  </td>
                </tr>
              ) : (
                filteredRiskLevels.map((risk) => (
                  <tr key={risk.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {risk.id}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {risk.name}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full">
                        {risk.rotation === null
                          ? "Not set"
                          : `Every ${risk.rotation} days`}
                      </span>
                    </td>
                    {user?.permissions?.includes('program_frequency.edit_program_frequency') && (
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditModal(risk)}
                          className="p-2 text-blue-600 rounded-md hover:bg-blue-50"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {modalOpen && selectedRisk && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Risk Level: {selectedRisk.name}
              </h3>
              <button
                onClick={closeModal}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <form className="flex flex-col gap-4" onSubmit={handleSave}>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Risk Level ID
                </label>
                <input
                  type="text"
                  value={selectedRisk.id}
                  disabled
                  className="px-3 h-10 text-sm text-gray-700 bg-gray-100 rounded-md border border-gray-300 focus:outline-none"
                />
                <p className="m-0 text-xs text-gray-500">
                  Risk level ID from Athenahealth (read-only).
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Risk Level Name
                </label>
                <input
                  type="text"
                  value={selectedRisk.name}
                  disabled
                  className="px-3 h-10 text-sm text-gray-700 bg-gray-100 rounded-md border border-gray-300 focus:outline-none"
                />
                <p className="m-0 text-xs text-gray-500">
                  Risk level name from Athenahealth (read-only).
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Rotation Frequency (Days)
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min="1"
                    value={frequencyInput}
                    onChange={(e) => setFrequencyInput(e.target.value)}
                    className="px-3 w-full h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">days</span>
                </div>
                <p className="m-0 text-xs text-gray-500">
                  Number of days until next follow-up visit is suggested.
                </p>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-3 justify-end items-center">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!user?.permissions?.includes('program_frequency.edit_program_frequency')}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramFrequency;
